import React from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import remediationData, { OS_LIST } from "./remediationData";
import { GET_USER_DETAILS } from "../../apis/dashboard-apis/dashboardDetailApi";
import Role from "../../utils/roles";

// The five endpoint checks, linked to their evidence pages (/checks/:checkType).
const MANUAL_EVIDENCE_CHECKS = [
  { id: "disk_encryption_enabled", label: "Disk Encryption" },
  { id: "antivirus_installed", label: "Antivirus" },
  { id: "firewall_enabled", label: "Firewall" },
  { id: "auto_update_enabled", label: "Auto Update" },
  { id: "screen_timeout_enabled", label: "Screen Lock" },
];

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace";

// Original OS brand marks, drawn inline so they render without external assets
// (keeps the public page CSP-safe and offline-friendly).
const OSGlyph = ({ os, size = 20 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    "aria-hidden": true,
    style: { display: "block" },
  };
  if (os === "windows") {
    return (
      <svg {...common}>
        <path
          fill="#0078D4"
          d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"
        />
      </svg>
    );
  }
  if (os === "mac") {
    return (
      <svg {...common}>
        <path
          fill="#333333"
          d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
        />
      </svg>
    );
  }
  // linux — minimal Tux
  return (
    <svg {...common}>
      <path
        fill="#1A1A1A"
        d="M12 2c-2.8 0-4.5 2.2-4.5 5.2 0 1.6-.7 2.6-1.6 3.9C4.7 12.7 4 14.4 4 16.2c0 .8.3 1.4.9 1.8C4.3 18.6 4 19.2 4 19.8c0 1.2 1.2 2 3.2 2h9.6c2 0 3.2-.8 3.2-2 0-.6-.3-1.2-.9-1.8.6-.4.9-1 .9-1.8 0-1.8-.7-3.5-1.9-5.1-.9-1.3-1.6-2.3-1.6-3.9C16.5 4.2 14.8 2 12 2z"
      />
      <ellipse cx="12" cy="15.2" rx="3.5" ry="4.3" fill="#fff" />
      <ellipse cx="10.2" cy="7.2" rx="1.4" ry="1.8" fill="#fff" />
      <ellipse cx="13.8" cy="7.2" rx="1.4" ry="1.8" fill="#fff" />
      <circle cx="10.6" cy="7.6" r=".75" fill="#1A1A1A" />
      <circle cx="13.4" cy="7.6" r=".75" fill="#1A1A1A" />
      <path d="M10.7 8.7h2.6l-1.3 1.9z" fill="#F6A821" />
      <path
        d="M9.2 20.9c-.9.5-2.2.3-2.5-.4-.3-.7.7-1.3 1.7-1.5.8-.1 1.5.2 1.5.7 0 .5-.3.9-.7 1.2z"
        fill="#F6A821"
      />
      <path
        d="M14.8 20.9c.9.5 2.2.3 2.5-.4.3-.7-.7-1.3-1.7-1.5-.8-.1-1.5.2-1.5.7 0 .5.3.9.7 1.2z"
        fill="#F6A821"
      />
    </svg>
  );
};

// Renders a copyable, monospaced command block.
const CommandBlock = ({ label, code }) => {
  const [copied, setCopied] = React.useState(false);
  const theme = useTheme();

  const handleCopy = () => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const lines = code.split("\n");

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #1E2A4E",
        boxShadow: "0 6px 20px rgba(11, 20, 55, 0.18)",
      }}
    >
      {/* Terminal title bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.75,
          py: 1,
          bgcolor: "#0A1030",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {["#FF5F56", "#FFBD2E", "#27C93F"].map((c) => (
            <Box
              key={c}
              sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: c }}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <TerminalOutlinedIcon sx={{ fontSize: 14, color: "#64748B" }} />
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 11.5,
              fontWeight: 600,
              color: "#94A3B8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label || "shell"}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={copied ? "Copied!" : "Copy"} placement="left">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ color: copied ? "#27C93F" : "#94A3B8", p: 0.5 }}
          >
            {copied ? (
              <CheckIcon fontSize="inherit" />
            ) : (
              <ContentCopyIcon fontSize="inherit" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Command body */}
      <Box
        sx={{
          bgcolor: "#0B1437",
          color: "#E2E8F0",
          px: 2,
          py: 1.5,
          fontFamily: MONO,
          fontSize: 13,
          lineHeight: 1.8,
          overflowX: "auto",
        }}
      >
        {lines.map((line, i) => (
          <Box
            key={i}
            sx={{ display: "flex", gap: 1.25, whiteSpace: "pre" }}
          >
            <Box
              component="span"
              sx={{ color: "#4F7CFF", userSelect: "none", flexShrink: 0 }}
            >
              $
            </Box>
            <Box component="span">{line}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// How to get to a command line on each OS. Shown next to the "Command line"
// heading so every check carries it — the commands themselves are useless to
// anyone who does not already know where to type them, and that is exactly the
// person following a remediation page.
const SHELL_HINT = {
  mac: "Applications → Utilities → Terminal",
  windows: 'Win+R → type "cmd" → Ctrl+Shift+Enter to run as administrator',
  linux: "Ctrl+Alt+T, or search for Terminal in your applications",
};

const CheckAccordion = ({ check, expanded, onToggle, osKey }) => {
  const theme = useTheme();
  return (
    <Accordion
      id={check.id}
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${
          expanded ? theme.palette.primary.main : theme.customColors.border.subtle
        }`,
        borderRadius: "10px !important",
        mb: 1.5,
        scrollMarginTop: 16,
        boxShadow: expanded
          ? "0 4px 16px rgba(3, 27, 137, 0.08)"
          : "0 1px 3px rgba(0, 0, 0, 0.04)",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: theme.customColors.text.iconMuted }} />
        }
        sx={{
          px: 2,
          bgcolor: expanded
            ? theme.customColors.background.cardHeader
            : "transparent",
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5 },
          py: 0.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            bgcolor: theme.palette.primary.main + "12",
            flexShrink: 0,
          }}
        >
          {check.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              color: theme.customColors.text.primary,
              letterSpacing: "-0.01em",
            }}
          >
            {check.title}
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 11.5,
              color: theme.customColors.text.disabled,
              mt: 0.25,
            }}
          >
            {check.id}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 1.5 }}>
        {/* Why it matters */}
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            p: 1.5,
            mb: 2,
            borderRadius: "10px",
            bgcolor: theme.customColors.status.warning.bg,
            border: `1px solid ${theme.customColors.status.warning.main}22`,
          }}
        >
          <LightbulbOutlinedIcon
            fontSize="small"
            sx={{ color: theme.customColors.status.warning.text, mt: "2px" }}
          />
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, lineHeight: 1.6 }}
          >
            {check.why}
          </Typography>
        </Box>

        {/* Step groups */}
        {check.steps.map((group, gi) => (
          <Box key={gi} sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: 14,
                mb: 1,
                color: theme.customColors.text.primary,
              }}
            >
              {group.heading}
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
              {group.items.map((item, ii) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={ii}
                  sx={{
                    mb: 0.75,
                    color: theme.customColors.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>

            {/* A screenshot of the settings pane the steps describe. Optional —
                groups without one render exactly as before. `caption` says what
                the reader should be looking at, since the point of the picture is
                to confirm they are in the right place. */}
            {group.image && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  component="img"
                  src={group.image}
                  alt={group.imageAlt || group.heading}
                  loading="lazy"
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 1.5,
                    border: `1px solid ${theme.customColors.border.subtle}`,
                  }}
                />
                {group.imageCaption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      color: theme.customColors.text.secondary,
                      fontStyle: "italic",
                    }}
                  >
                    {group.imageCaption}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ))}

        {/* Commands */}
        {check.commands?.length > 0 && (
          <>
            <Divider sx={{ my: 1.5, borderColor: theme.customColors.border.subtle }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
              <TerminalOutlinedIcon
                sx={{ fontSize: 17, color: theme.palette.primary.main }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: theme.customColors.text.primary,
                }}
              >
                Command line
              </Typography>
              {SHELL_HINT[osKey] && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  ({SHELL_HINT[osKey]})
                </Typography>
              )}
            </Box>
            {check.commands.map((c, ci) => (
              <CommandBlock key={ci} label={c.label} code={c.code} />
            ))}
          </>
        )}

        {/* Verify */}
        {check.verify && (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.5,
              mt: 1,
              borderRadius: "10px",
              bgcolor: theme.customColors.status.success.bg,
              border: `1px solid ${theme.customColors.status.success.main}22`,
            }}
          >
            <CheckCircleOutlineIcon
              fontSize="small"
              sx={{ color: theme.customColors.status.success.text, mt: "2px" }}
            />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: theme.customColors.status.success.text,
                  display: "block",
                }}
              >
                HOW TO VERIFY
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.customColors.text.secondary, lineHeight: 1.6 }}
              >
                {check.verify}
              </Typography>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// Manually added assets have no agent reporting, so their checks sit Pending and
// the OS commands below can't clear them. Auto-expanded via ?source=manual.
// Employees can't access the checks page to upload evidence — admins can.
const ManualAssetSection = ({ defaultExpanded = false, isAdmin = false }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((v) => !v)}
      disableGutters
      elevation={0}
      sx={{
        mb: 3,
        border: `1px solid ${theme.palette.primary.main}33`,
        borderRadius: "14px !important",
        bgcolor: theme.customColors.background.header,
        boxShadow: expanded
          ? "0 4px 16px rgba(3, 27, 137, 0.08)"
          : "0 1px 3px rgba(0, 0, 0, 0.04)",
        transition: "box-shadow 0.2s ease",
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: theme.customColors.text.iconMuted }} />
        }
        sx={{
          px: 2,
          py: 0.5,
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5 },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.primary.main + "12",
            flexShrink: 0,
          }}
        >
          <UploadFileOutlinedIcon
            sx={{ fontSize: 22, color: theme.palette.primary.main }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 15,
              letterSpacing: "-0.01em",
              color: theme.customColors.text.primary,
            }}
          >
            Manually added asset
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              mt: 0.25,
              color: theme.customColors.text.disabled,
            }}
          >
            {isAdmin
              ? "No agent reporting — upload evidence or disable the asset"
              : "Complete the steps below and share evidence with your admin"}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.6,
              color: theme.customColors.text.secondary,
            }}
          >
            This device was added manually, so no Regodit agent reports on it. Its
            checks stay <strong>Pending</strong> and can&apos;t be verified
            automatically — the OS steps below won&apos;t clear them on their own.
          </Typography>

          <Typography
            sx={{
              mt: 2,
              fontWeight: 700,
              fontSize: 14,
              color: theme.customColors.text.primary,
            }}
          >
            How to fix{isAdmin ? " — do either:" : ":"}
          </Typography>

          {isAdmin ? (
            <Box component="ol" sx={{ pl: 2.5, m: 0, mt: 1 }}>
              <Typography
                component="li"
                sx={{
                  mb: 1.25,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: theme.customColors.text.secondary,
                }}
              >
                <strong>Upload evidence</strong> for the check to mark it verified:
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {MANUAL_EVIDENCE_CHECKS.map((c) => (
                    <MuiLink
                      key={c.id}
                      href={`/checks/${c.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "8px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.primary.main}33`,
                        bgcolor: theme.customColors.background.main,
                        "&:hover": { bgcolor: theme.palette.primary.main + "0F" },
                      }}
                    >
                      {c.label}
                    </MuiLink>
                  ))}
                </Box>
              </Typography>

              <Typography
                component="li"
                sx={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: theme.customColors.text.secondary,
                }}
              >
                <strong>Disable the asset</strong> — if this device shouldn&apos;t be
                evaluated, disable it so its checks stop counting toward compliance.
              </Typography>
            </Box>
          ) : (
            <Box component="ol" sx={{ pl: 2.5, m: 0, mt: 1 }}>
              <Typography
                component="li"
                sx={{
                  mb: 1.25,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: theme.customColors.text.secondary,
                }}
              >
                <strong>Complete the remediation steps below</strong> for your
                operating system to configure each security check on your device.
              </Typography>

              <Typography
                component="li"
                sx={{
                  mb: 1.25,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: theme.customColors.text.secondary,
                }}
              >
                <strong>Collect evidence</strong> (screenshots, settings exports,
                etc.) showing each check is properly configured.
              </Typography>

              <Typography
                component="li"
                sx={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: theme.customColors.text.secondary,
                }}
              >
                <strong>Share the evidence with your admin</strong> — they can
                upload it on your behalf to verify the checks. You don&apos;t have
                access to the checks page from your employee account.
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const AgentRemediationSteps = () => {
  const theme = useTheme();
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const isAdmin = (userData?.getAccountDetails?.userDetails?.roles || []).some(
    (role) => role?.role_description === Role.ADMIN
  );

  // Deep-linking: /agentRemediationSteps?os=windows&check=firewall_enabled
  // preselects the OS tab and auto-expands the matching check.
  const [searchParams] = useSearchParams();
  const checkParam = searchParams.get("check") || null;
  const osKey = (searchParams.get("os") || "").toLowerCase();
  const isManualAsset =
    (searchParams.get("source") || "").toLowerCase() === "manual";
  const initialOsIndex = React.useMemo(() => {
    const idx = OS_LIST.findIndex((o) => o.key === osKey);
    return idx === -1 ? 0 : idx;
  }, [osKey]);

  const [osIndex, setOsIndex] = React.useState(initialOsIndex);
  const [expandedId, setExpandedId] = React.useState(checkParam);
  const activeOs = OS_LIST[osIndex];
  const checks = remediationData[activeOs.key] || [];

  // Scroll the deep-linked check into view once it exists in the active OS list.
  React.useEffect(() => {
    if (!checkParam) return;
    if (!checks.some((c) => c.id === checkParam)) return;
    const t = setTimeout(() => {
      document
        .getElementById(checkParam)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [checkParam, checks]);

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: theme.customColors.background.header,
        backgroundImage:
          "radial-gradient(circle, rgba(3,27,137,0.06) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "18px",
            border: `1px solid ${theme.customColors.border.subtle}`,
            bgcolor: theme.customColors.background.main,
            p: { xs: 2.5, md: 4 },
            boxShadow: "0 4px 24px rgba(3, 27, 137, 0.05)",
          }}
        >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: theme.palette.primary.main + "12",
                flexShrink: 0,
              }}
            >
              <ShieldOutlinedIcon
                sx={{ fontSize: 26, color: theme.palette.primary.main }}
              />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 22, md: 26 },
                  letterSpacing: "-0.02em",
                  color: theme.customColors.text.primary,
                }}
              >
                Endpoint System Remediation Steps
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  maxWidth: 620,
                  fontSize: 14.5,
                  lineHeight: 1.6,
                  color: theme.customColors.text.secondary,
                }}
              >
                Fix the compliance checks flagged by the Regodit agent on your
                device. Follow the steps for your operating system below.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Manual assets — evidence / disable, instead of agent-driven fixes.
            Collapsed by default; auto-expanded when deep-linked with ?source=manual. */}
        <ManualAssetSection defaultExpanded={isManualAsset} isAdmin={isAdmin} />

        {/* OS tabs — segmented control */}
        <Box
          sx={{
            borderRadius: "14px",
            border: `1px solid ${theme.customColors.border.subtle}`,
            bgcolor: theme.customColors.background.header,
            p: 0.75,
            mb: 3,
          }}
        >
          <Tabs
            value={osIndex}
            onChange={(_, v) => setOsIndex(v)}
            variant="fullWidth"
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={{
              minHeight: 0,
              "& .MuiTabs-flexContainer": { gap: 0.75 },
              "& .MuiTab-root": {
                fontWeight: 700,
                fontSize: 15,
                textTransform: "none",
                py: 1.5,
                minHeight: 0,
                borderRadius: "10px",
                color: theme.customColors.text.secondary,
                transition: "all 0.2s ease",
              },
              "& .MuiTab-root:hover": {
                color: theme.customColors.text.primary,
                bgcolor: "rgba(3, 27, 137, 0.05)",
              },
              "& .Mui-selected": {
                color: theme.palette.primary.main,
                bgcolor: theme.customColors.background.main,
                boxShadow: "0 2px 6px rgba(3, 27, 137, 0.12)",
              },
            }}
          >
            {OS_LIST.map((os) => (
              <Tab
                key={os.key}
                disableRipple
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      lineHeight: 1,
                    }}
                  >
                    <OSGlyph os={os.key} size={20} />
                    {os.label}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Checks */}
        {checks.map((check) => (
          <CheckAccordion
            key={check.id}
            check={check}
            osKey={activeOs.key}
            expanded={expandedId === check.id}
            onToggle={() =>
              setExpandedId((prev) => (prev === check.id ? null : check.id))
            }
          />
        ))}

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 2.5,
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            Still failing after these steps? Contact your{" "}
            <MuiLink
              href="mailto:connect@solsphere.ai"
              underline="hover"
              sx={{ fontWeight: 600, color: theme.palette.primary.main }}
            >
              IT / Compliance team
            </MuiLink>
            .
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              color: theme.customColors.text.disabled,
              letterSpacing: "normal",
            }}
          >
            © {new Date().getFullYear()} Regodit
          </Typography>
        </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AgentRemediationSteps;
